<script lang="ts">
	import { setContext, type Snippet } from "svelte";
	import { twMerge } from "tailwind-merge";

	interface Props {
		open: boolean;
		style?: string;
		isDesktop: boolean;
		// fullBleed makes the drawer clip to its rounded corners and paint the full
		// page background behind the content, and inset the content from the top so it
		// clears the overlaid exit button. Defaults off.
		fullBleed?: boolean;
		// frameContent adds a bottom inset so the background frames the content on
		// every side. Only set it when the background is distinct from the white card
		// (see hasBackgroundFill), otherwise the submit stays flush like a plain tray.
		frameContent?: boolean;
		onShow?: () => void;
		onHide?: () => void;
		content?: Snippet<[() => void]>;
	}

	let { open = $bindable(false), isDesktop = false, fullBleed = false, frameContent = false, onShow, onHide, content, style }: Props = $props();

	// The page background vars arrive via style. Paint them onto the drawer so the
	// image fills behind the rounded corners and overlaid header.
	const fullBleedBackground = "background-image: var(--background-image); background-position: var(--background-position); background-repeat: var(--background-repeat); background-size: var(--background-size); background-attachment: var(--background-attachment);";

	export function close() {
		open = false;
	}

	function handleTransitionEnd() {
		if (open) {
			onShow?.();
		} else {
			onHide?.();
		}
	}

	setContext("close", close);
</script>

{#if isDesktop}
	<div
		class={twMerge("absolute right-0 bottom-0 left-0 z-2 mx-auto h-full max-h-150 max-w-2xl space-y-4 overflow-clip border bg-(--background-color) transition-transform duration-300 ease-in-out", open ? "-translate-y-1/2" : "translate-y-full", fullBleed && "pt-4", fullBleed && frameContent && "pb-4")}
		style={(style ?? "") + (fullBleed ? fullBleedBackground : "")}
		ontransitionend={handleTransitionEnd}
	>
		{@render content?.(close)}
	</div>
{:else}
	<div class={twMerge("drawer absolute right-0 bottom-0 left-0 z-2 mx-auto h-[95%] max-w-md rounded-t-2xl bg-(--background-color) transition-transform duration-300 ease-in-out", open ? "translate-y-0" : "translate-y-full", fullBleed && "overflow-clip pt-4", fullBleed && frameContent && "pb-4")} style={(style ?? "") + (fullBleed ? fullBleedBackground : "")} ontransitionend={handleTransitionEnd}>
		{@render content?.(close)}
	</div>
{/if}

<style>
	.drawer {
		box-shadow: 0px 0 10px rgba(0, 0, 0, 0.3);
	}
</style>
